/**
 * @file 回复区
 * @author yangholmes 2023-12-18
 */

import { createRef, useContext, useState, useRef, forwardRef, useImperativeHandle} from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import CommonIcon from '../CommonIcon';
import { getThreadReply, postThreadReply, ThreadReplySortEnum } from '@/api/thread';
import { IUserLoginInfo } from '@/api/user';
import useLoginAuth from '@/hooks/useLoginAuth';
import Styles from './index.module.scss';
import { Editor, EditorExpose } from './Editor';
import CommonImage, { ImageType } from '../CommonImage';
import { ActionTypeValues, StoreContext, namespace } from '@/context';
import { safePushNewReply } from '@/utils/thread';
import Toast from '../Toast';
import userCenter from '@/constants/userCenter';
import errorCode from '@/constants/errorCode';
import { getErrorMessage } from '@/constants/errorCodeMap';
import { formatDisplayNumber } from '@/utils/formatNumber';
import { SortFilter } from '../SortFilter/mIndex';
import { IMenuListItem } from '../CommonDropdown/mIndex';
import { ImageMagnifier } from '../ImageMagnifier';
import { IForumType, ICommentListRes } from '@/api/detail';
import { ThreadDetailCtx } from '@/context/threadDetail';
import { isStoreAppBannerEntry } from '@/utils/url';
import { getUserHomeUrl } from '@/constants/url';
import useSls, { MetricTypes, statusTypes } from '@/hooks/useSls';

const ListItem = dynamic(() => import('./listItem'), { ssr: false, loading: () => (
  <div className={cn(Styles.skeleton)} >
    <CommonIcon icon="comment-skeleton" className={Styles.icon} />
  </div>
)});

const limit = 20;
/** 默认头像 */
const noavatarImageUrl = `${process.env.RESOURCE_URL}uc_server/images/noavatar_middle.gif`

export interface ThreadCommentsExpose {
  onFocus: () => void;
}
export interface IThreadComments {
  replys: {
    firstTwoReplys: ICommentListRes[];
    totalReplys: number;
  };
  tid: string;
  account: IUserLoginInfo;
  title?: string;
  forumTypes?: IForumType[];
  authorid?: number;
}
const ThreadComments = forwardRef<ThreadCommentsExpose | undefined, IThreadComments>(function ThreadComments({
  replys,
  tid,
  title,
  forumTypes,
  authorid,
}, ref) {
  const t = useTranslations();
  const router = useRouter();

  const exitorRef = useRef<EditorExpose>(null)

  const { state, dispatch } = useContext(StoreContext);

  const account = state.app.account
  const { track, TRACK_EVENTS, extractThreadTags } = state[namespace.app].sensors || {};

  const { isLogin } = useLoginAuth(account);

  /** 回帖总数 */
  const [totalReplys, setTotalReplys] = useState(replys.totalReplys)

  const { reportSlsData } = useSls();

  /**
   * 更新评论总数
   * @param totalReplys 评论总数
   */
  const updateTotalReplys = (totalReplys: number) => {
    if (totalReplys < 0 ) {
      return
    }
    setTotalReplys(totalReplys)
    dispatch({
      namespace: namespace.detail,
      type: ActionTypeValues.UPDATE_TOTALREPLYS,
      payload: totalReplys
    })
  }

  /** 回帖列表 */
  const [replysList, setReplysList] = useState(replys.firstTwoReplys)

  /** 正在加载回帖 */
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  /** 评论列表排列顺序 */
  const [currentSort, setCurrentSort] = useState<string>(ThreadReplySortEnum.likesHot);
  const sortList: IMenuListItem[] = [
    {key: ThreadReplySortEnum.likesHot, text: t('marshalling_sequence_likes_hot')},
    {key: ThreadReplySortEnum.desc, text: t('marshalling_sequence_desc')},
    {key: ThreadReplySortEnum.asc, text: t('marshalling_sequence_asc')},
  ];
  
  /* 图片预览地址 */
  const [magImages, setMagImages] = useState<string[]>([]);

  const threadDetail = useContext(ThreadDetailCtx);

  useImperativeHandle(ref, () => ({
    onFocus: () => {
      exitorRef.current?.onFocus()
    },
  }))

  /** 渲染编辑器 */
  const renderEditor = () => {
    if (isLogin) {
      return <>
        <CommonImage
          className={Styles.avatar}
          src={account?.avatar?.large || noavatarImageUrl}
          layout="fill"
          cutMode='cover'
          type={ImageType.avatar}
        />
        <Editor
          className={Styles.editor}
          onPublish={publishNewReply}
          ref={exitorRef}
        />
      </>
    }
    return <>
      <CommonImage
        className={Styles.avatar}
        src={noavatarImageUrl}
        layout="fill"
        cutMode='cover'
        type={ImageType.avatar}
      />
      <div
        className={Styles.fakeEditor}
        onClick={() => {
          track?.(TRACK_EVENTS?.[namespace.detail].commentsSendResult.name, {
            thread_name: title,
            is_success: false,
            fail_reason: '未登录',
            thread_tid : tid,
            thread_tag_name: extractThreadTags?.(forumTypes),
            account_slug: authorid,
            comment_type: 'Thread',
          }, namespace.detail);
          window.open(userCenter().login, '_self')
        }}
      >
        {t('thread_comment_placeholder')}
      </div>
    </>
  }

  /** 渲染“加载更多”区域 */
  const renderLoadMore = () => {
    if (loading) {
      // 显示加载中
      return <div className={cn(Styles.skeleton)} >
        {
          Array.from({ length: 6 }, (_, index) => index).map((_, i) => (
            <CommonIcon key={i} icon="comment-skeleton" className={cn(Styles.icon, Styles.marginBot)} />
          ))
        }
      </div>
    }
    if (hasMore && replysList.length < totalReplys) {
      // 如果接口返回不为空且列表条数小于总条数时，显示加载更多
      return <div className={cn(Styles.moreWrapper)} onClick={onLoadMore}>
        {t('tips_thread_comment_more')}
        <div style={{height: '14px', width: '14px'}}>
          <CommonIcon
            icon="arrow-extend"
            className={Styles.extendIcon}
          />
        </div>
      </div>
    }
    if (replysList.length === 0) {
      return (
        <div className={Styles.commentEmpty}>
          <div className={Styles.emptyComment}>
            <CommonIcon icon="empty-comment" />
          </div>
          <p>{t('thread_comment_empty')}</p>
        </div>
      )
    }
    // 其他情况显示没有更多
    return <div className={cn(Styles.noMoreWrapper)} >
        {t('tips_thread_comment_no_more')}
      </div>
  }

  /** 获取评论 */
  const getReply = (offset: number, sort?: string) => {
    setLoading(true);
    setNewReplyPid(undefined);
    return getThreadReply(tid, offset, limit, false, sort).then(res => {
      return res
    }).finally(() => {
      setLoading(false);
    })
  }

  /** 新评论的 pid 用于自动滚动到视口 */
  const [newReplyPid, setNewReplyPid] = useState<string|undefined>()
  const newReplyList = useRef<ICommentListRes[]>([]);
  /**
   * 发布新评论
   */
  const publishNewReply = (ctx: string, pid?: string | number) => {
    setNewReplyPid(undefined);
    reportSlsData([
      ['reportMetric', MetricTypes.commentStatus, statusTypes.click],
    ])
    return postThreadReply(tid, ctx, pid).then(newReply => {
      const { item, ext } = newReply
      if (!!newReplyList.current.length) {
        newReplyList.current = [item, ...newReplyList.current];
      } else {
        newReplyList.current = [item];
      }
      getReply(0, currentSort).then(res => {
        const { items, total_items } = res
        if (!!newReplyList.current.length) {
          setReplysList(safePushNewReply(newReplyList.current, items));
        } else {
          setReplysList([...items]);
        }
        updateTotalReplys(total_items)
      })
      setNewReplyPid(item.pid)
      ext?.tips?.message && Toast.show({
        content: ext.tips.message
      })
      track?.(TRACK_EVENTS?.[namespace.detail].commentsSendResult.name, {
        thread_name: title,
        is_success: true,
        thread_tid : tid,
        thread_tag_name: extractThreadTags?.(forumTypes),
        account_slug: authorid,
        comment_type: !!pid ? 'Comment' : 'Thread',
      }, namespace.detail);
      reportSlsData([
        ['reportMetric', MetricTypes.commentStatus, statusTypes.success],
      ])
      return
    }).catch((err) => {
      const { message, code } = err
      message && Toast.show({
        content: message
      })
      track?.(TRACK_EVENTS?.[namespace.detail].commentsSendResult.name, {
        thread_name: title,
        is_success: false,
        fail_reason: getErrorMessage(code) || message,
        thread_tid : tid,
        thread_tag_name: extractThreadTags?.(forumTypes),
        account_slug: authorid,
        comment_type: !!pid ? 'Comment' : 'Thread',
      }, namespace.detail);
      // TODO 封装成公共函数
      if (code === errorCode.UNBIND_PHONE) {
        reportSlsData([
          ['reportMetric', MetricTypes.commentStatus, statusTypes.unErrorFail],
          ['setEnvironment',{
            [MetricTypes.failReason]: message || code  || '未知原因',
          }],
        ])
        // 处理一下未绑定手机的报错
        setTimeout(() => {
          window.open(userCenter().bindPhone, '_self')
        }, 1e3)
      } else if (code === errorCode.UNAUTHORIZED) {
        reportSlsData([
          ['reportMetric', MetricTypes.commentStatus, statusTypes.unErrorFail],
          ['setEnvironment',{
            [MetricTypes.failReason]: message || code  || '未知原因',
          }],
        ])
        // 处理一下未登录
        setTimeout(() => {
          window.open(userCenter().login, '_self')
        }, 1e3)
      } else {
        reportSlsData([
          ['reportMetric', MetricTypes.commentStatus, statusTypes.fail],
          ['setEnvironment',{
            [MetricTypes.failReason]: message || code  || '未知原因',
          }],
        ])
      }
      throw err
    })
  }

  const onDeleteReply = (index: number) => {
    const _replysList = [...replysList]
    _replysList.splice(index, 1)
    setReplysList([..._replysList])
    updateTotalReplys(totalReplys - 1)
  }

  const [rreplyIndex, setRreplyIndex] = useState<number>();
  const onClickRreply = (index: number) => {
    if (index === rreplyIndex) {
      setRreplyIndex(undefined);
      return;
    }
    setRreplyIndex(index);
  }
  const onClickRreplyPublish = (context: string, i: number) => {
    const pid = replysList[i].pid;
    const ctx = context;
    return publishNewReply(ctx, pid).then(() => {
      setRreplyIndex(undefined);
    })
  }

  const onLoadMore = () => {
    // 查看更多评论pc端埋点
    track?.(TRACK_EVENTS?.[namespace.detail].readMoreComments.name, {
      thread_tid: threadDetail?.tid,
      thread_name: threadDetail?.subject,
      account_slug: threadDetail?.author?.id,
      is_featured: !!threadDetail?.digest,
      list_name: (threadDetail?.forum_types || [])[0]?.name || t('btn_home'),
    }, namespace.detail);
    const offset = replysList.length
    setLoading(true);
    getReply(offset, currentSort).then(res => {
      const { items, total_items } = res
      items.length === 0 && setHasMore(false);
      updateTotalReplys(total_items);
      setReplysList(safePushNewReply(replysList, items))
    })
  }

  const onLikeTrack = (status: boolean, isSuccess: boolean, failReason?: string) => {
    track?.(TRACK_EVENTS?.[namespace.detail].threadLikeResult.name, {
      thread_name: title,
      is_success: isSuccess,
      fail_reason: failReason,
      thread_tid : tid,
      thread_tag_name: extractThreadTags?.(forumTypes),
      account_slug: authorid,
      button_name: status ? 'Like' : 'Unlike',
      position: 'Comment',
    }, namespace.detail);
  }

  return (
    <div className={cn(Styles.root, Styles.anchorContainer)}>
      <div id="reply-list" className={Styles.anchor} />
      <div className={Styles.commentsTotalWrapper}>
        <p className={Styles.commentsTotal}>
          {!!totalReplys ? t("total_comments", {count: formatDisplayNumber(totalReplys)}) : t("label_comments")}
        </p>
        <SortFilter
          sortList={sortList}
          onChange={
            (key) => {
              track?.(TRACK_EVENTS?.[namespace.detail].selectListClick.name, {
                button_name: key === ThreadReplySortEnum.desc ? '最新' : key === ThreadReplySortEnum.asc ? '最早' : '最热',
                position: 'Comment',
              }, namespace.detail);
              setCurrentSort(key);
              setReplysList([]);
              newReplyList.current = [];
              getReply(0, key).then(res => {
                const { items, total_items } = res
                setReplysList([...items])
                updateTotalReplys(total_items)
              })
            }
          } 
        />
      </div>
      {
        /** 回复编辑器 */
        <div
          className={Styles.replyEditor}
        >
          {renderEditor()}
        </div>
      }
      <div onClick={(evt) => {
        const target = evt.target as HTMLImageElement;
        if (target && target.tagName.toLowerCase() === 'img' && target.classList.contains('reply-content-img')) {
          setMagImages([target.src]);
        }  else if (target.tagName.toLowerCase() === 'span' && target.getAttribute('type') === 'quoted-user-name') {
          // 评论引用-跳转被引用者用户主页
          const dataId = target.getAttribute('data-user-id');
          const dataUuid = target.getAttribute('data-user-uuid');
          track?.(TRACK_EVENTS?.[namespace.detail].userProfileEntryClick.name, {
            thread_tid: tid,
            thread_name: title,
            account_slug: authorid,
            list_name: (threadDetail?.forum_types || [])[0]?.name || t('btn_home'),
            position: 'Reply',
          }, namespace.detail);
          if(!!dataId && !isStoreAppBannerEntry(router.query.entry)) {
            window.open(getUserHomeUrl(Number(dataId), dataUuid as string), '_blank');
          }
        }
      }}>{
        /* 评论列表 */
        replysList.map((reply, i) => {
          const ref = createRef<HTMLDivElement>();
          if (reply.pid === newReplyPid) {
            // 如果新评论 id 命中列表中某个评论，且不在视口中，则跳转到视口中
            setTimeout(() => {
              ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setNewReplyPid(undefined);
              ref.current?.parentElement?.classList.add(cn(Styles.newReply))
            }, 0)
          }
          return <div
            key={reply.pid}
            className={cn(Styles.anchorContainer)}
          >
            <div ref={ref} className={cn(Styles.anchor)} />
            <ListItem
              reply={reply}
              isRreply={rreplyIndex === i}
              onDeleteReply={() => onDeleteReply(i)}
              onClickRreply={() => onClickRreply(i)}
              onClickPublish={(ctx) => onClickRreplyPublish(ctx, i)}
              onLikeTrack={onLikeTrack}
            />
          </div>
        })
      }</div>
      {
        renderLoadMore()
      }
      {
        /** 图片放大镜 */
        <ImageMagnifier images={magImages} onClose={() => {setMagImages([])}} />
      }
    </div>
  )
})

export default ThreadComments;
